import React, { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Search, TrendingUp, MapPin, BarChart3, ArrowRight, Briefcase, GraduationCap,
    IndianRupee, Clock, CheckCircle2, Star, ChevronRight, ArrowLeft, Users, Zap
} from 'lucide-react';
import SEO from '../components/SEO';

// Static career data for SEO pages
const careerDatabase = {
    'software-engineer': {
        title: 'Software Engineer',
        description: 'Design, develop, and maintain software applications. One of the most in-demand careers in India.',
        salaryRange: { min: 500000, max: 4500000 },
        avgSalary: 1200000,
        demand: '+22%',
        experience: '0-15+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Pune', 'Mumbai', 'Delhi NCR'],
        topCompanies: ['Google', 'Amazon', 'Microsoft', 'Flipkart', 'Infosys'],
        skills: ['JavaScript', 'Python', 'Java', 'System Design', 'Data Structures', 'REST APIs', 'Git', 'SQL'],
        roadmap: ['Learn fundamentals (2-4 weeks)', 'Data Structures & Algorithms (4-6 weeks)', 'Build projects (4-6 weeks)', 'System Design basics (2-4 weeks)', 'Interview preparation (2-4 weeks)'],
        category: 'Technology',
        growth: 'High',
        remoteFriendly: true,
    },
    'data-scientist': {
        title: 'Data Scientist',
        description: 'Analyze complex data sets to solve business problems using statistics, ML, and AI.',
        salaryRange: { min: 600000, max: 4200000 },
        avgSalary: 1400000,
        demand: '+27%',
        experience: '0-12+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Mumbai', 'Delhi NCR', 'Chennai'],
        topCompanies: ['Google', 'Amazon', 'Flipkart', 'Swiggy', 'PhonePe'],
        skills: ['Python', 'Statistics', 'Machine Learning', 'SQL', 'TensorFlow', 'Pandas', 'Deep Learning', 'NLP'],
        roadmap: ['Python & Statistics (3-4 weeks)', 'Data Analysis with Pandas (2-3 weeks)', 'Machine Learning fundamentals (4-6 weeks)', 'Deep Learning & NLP (4-6 weeks)', 'Portfolio projects (3-4 weeks)'],
        category: 'Data & AI',
        growth: 'Very High',
        remoteFriendly: true,
    },
    'product-manager': {
        title: 'Product Manager',
        description: 'Own the product vision, strategy, and roadmap. Bridge between engineering, design, and business.',
        salaryRange: { min: 800000, max: 5000000 },
        avgSalary: 1800000,
        demand: '+18%',
        experience: '2-15+ years',
        topCities: ['Bangalore', 'Mumbai', 'Delhi NCR', 'Hyderabad', 'Pune'],
        topCompanies: ['Google', 'Swiggy', 'Flipkart', 'Razorpay', 'CRED'],
        skills: ['Product Strategy', 'User Research', 'Data Analysis', 'SQL', 'A/B Testing', 'Wireframing', 'Stakeholder Management'],
        roadmap: ['Understand product fundamentals (2-3 weeks)', 'Learn user research methods (2-3 weeks)', 'Data-driven decision making (3-4 weeks)', 'Build product case studies (3-4 weeks)', 'Mock PM interviews (2-3 weeks)'],
        category: 'Product',
        growth: 'High',
        remoteFriendly: true,
    },
    'frontend-developer': {
        title: 'Frontend Developer',
        description: 'Build user-facing web applications with modern JavaScript frameworks.',
        salaryRange: { min: 400000, max: 3500000 },
        avgSalary: 1000000,
        demand: '+20%',
        experience: '0-12+ years',
        topCities: ['Bangalore', 'Pune', 'Delhi NCR', 'Hyderabad', 'Mumbai'],
        topCompanies: ['Google', 'Microsoft', 'Flipkart', 'Zomato', 'Razorpay'],
        skills: ['React', 'JavaScript', 'TypeScript', 'CSS', 'HTML', 'Next.js', 'Testing', 'Performance Optimization'],
        roadmap: ['HTML/CSS/JS basics (3-4 weeks)', 'React fundamentals (3-4 weeks)', 'Advanced React patterns (2-3 weeks)', 'TypeScript & Testing (2-3 weeks)', 'Build portfolio projects (3-4 weeks)'],
        category: 'Technology',
        growth: 'High',
        remoteFriendly: true,
    },
    'devops-engineer': {
        title: 'DevOps Engineer',
        description: 'Automate infrastructure, CI/CD pipelines, and ensure system reliability at scale.',
        salaryRange: { min: 600000, max: 4000000 },
        avgSalary: 1500000,
        demand: '+25%',
        experience: '1-12+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Pune', 'Delhi NCR', 'Chennai'],
        topCompanies: ['Amazon', 'Google', 'Microsoft', 'Flipkart', 'Ola'],
        skills: ['Docker', 'Kubernetes', 'AWS/Azure', 'CI/CD', 'Terraform', 'Linux', 'Python', 'Monitoring'],
        roadmap: ['Linux & Networking (2-3 weeks)', 'Docker & Containers (2-3 weeks)', 'CI/CD Pipelines (2-3 weeks)', 'Cloud platforms (AWS/GCP) (4-6 weeks)', 'Kubernetes & IaC (3-4 weeks)'],
        category: 'Technology',
        growth: 'Very High',
        remoteFriendly: true,
    },
    'ux-designer': {
        title: 'UX Designer',
        description: 'Create intuitive, user-centered digital experiences through research, design, and testing.',
        salaryRange: { min: 400000, max: 3000000 },
        avgSalary: 900000,
        demand: '+20%',
        experience: '0-10+ years',
        topCities: ['Bangalore', 'Mumbai', 'Delhi NCR', 'Pune', 'Hyderabad'],
        topCompanies: ['Google', 'Microsoft', 'Flipkart', 'Swiggy', 'PhonePe'],
        skills: ['Figma', 'User Research', 'Wireframing', 'Prototyping', 'Design Systems', 'Usability Testing', 'Information Architecture'],
        roadmap: ['Design fundamentals (2-3 weeks)', 'Figma mastery (2-3 weeks)', 'User research methods (3-4 weeks)', 'Build design portfolio (4-6 weeks)', 'UX case studies (2-3 weeks)'],
        category: 'Design',
        growth: 'High',
        remoteFriendly: true,
    },
    'cybersecurity-analyst': {
        title: 'Cybersecurity Analyst',
        description: 'Protect organizations from cyber threats through monitoring, analysis, and incident response.',
        salaryRange: { min: 500000, max: 3500000 },
        avgSalary: 1100000,
        demand: '+31%',
        experience: '0-12+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Delhi NCR', 'Mumbai', 'Chennai'],
        topCompanies: ['Palo Alto Networks', 'CrowdStrike', 'TCS', 'Wipro', 'HCL'],
        skills: ['Network Security', 'SIEM', 'Penetration Testing', 'Python', 'Incident Response', 'Risk Assessment', 'Cloud Security'],
        roadmap: ['Networking fundamentals (3-4 weeks)', 'Security concepts & tools (3-4 weeks)', 'Ethical hacking basics (4-6 weeks)', 'Cloud security (3-4 weeks)', 'Certifications prep (4-6 weeks)'],
        category: 'Security',
        growth: 'Very High',
        remoteFriendly: true,
    },
    'cloud-architect': {
        title: 'Cloud Architect',
        description: 'Design and oversee cloud computing strategies including cloud adoption plans and management.',
        salaryRange: { min: 1200000, max: 5500000 },
        avgSalary: 2200000,
        demand: '+29%',
        experience: '3-15+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Mumbai', 'Delhi NCR', 'Pune'],
        topCompanies: ['Amazon', 'Google', 'Microsoft', 'Accenture', 'Deloitte'],
        skills: ['AWS', 'Azure', 'GCP', 'Terraform', 'Microservices', 'Docker', 'Kubernetes', 'System Design'],
        roadmap: ['Cloud fundamentals (3-4 weeks)', 'AWS/Azure deep dive (6-8 weeks)', 'Architecture patterns (4-6 weeks)', 'Security & compliance (2-3 weeks)', 'Certification prep (4-6 weeks)'],
        category: 'Technology',
        growth: 'Very High',
        remoteFriendly: true,
    },
    'system-design-engineer': {
        title: 'System Design Engineer',
        description: 'Design highly scalable, fault-tolerant, and distributed software systems for enterprise applications.',
        salaryRange: { min: 2000000, max: 8000000 },
        avgSalary: 3500000,
        demand: '+28%',
        experience: '5-15+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Pune', 'Gurugram', 'Seattle'],
        topCompanies: ['Amazon', 'Uber', 'Netflix', 'Meta', 'Atlassian'],
        skills: ['Distributed Systems', 'Microservices', 'Database Design', 'Caching', 'Message Queues', 'CAP Theorem', 'Load Balancing'],
        roadmap: ['Core networking & protocols (2-3 weeks)', 'Database scaling & sharding (3-4 weeks)', 'Caching & Message Queues (2-3 weeks)', 'Microservices Architecture (3-4 weeks)', 'Case Studies & Mock Interviews (4-6 weeks)'],
        category: 'Technology',
        growth: 'Very High',
        remoteFriendly: true,
    },
    'ml-architecture': {
        title: 'ML Architect',
        description: 'Design and deploy robust end-to-end Machine Learning pipelines and infrastructure at scale.',
        salaryRange: { min: 2500000, max: 10000000 },
        avgSalary: 4500000,
        demand: '+35%',
        experience: '6-15+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Pune', 'San Francisco', 'London'],
        topCompanies: ['OpenAI', 'Google', 'Microsoft', 'NVIDIA', 'Anthropic'],
        skills: ['TensorFlow/PyTorch', 'MLOps', 'Kubernetes', 'Model Serving', 'Data Pipelines', 'GPU Optimization', 'LLM Architecture'],
        roadmap: ['Advanced ML algorithms (4-5 weeks)', 'Model training optimization (3-4 weeks)', 'MLOps & CI/CD for ML (4-5 weeks)', 'Model serving & scaling (3-4 weeks)', 'LLM fine-tuning & RAG (4-6 weeks)'],
        category: 'Data & AI',
        growth: 'Explosive',
        remoteFriendly: true,
    },
    'tpm': {
        title: 'Technical Program Manager (TPM)',
        description: 'Bridge the gap between engineering and business, managing complex technical delivery across teams.',
        salaryRange: { min: 1800000, max: 7000000 },
        avgSalary: 3000000,
        demand: '+22%',
        experience: '5-15+ years',
        topCities: ['Bangalore', 'Hyderabad', 'Gurugram', 'Mumbai', 'Chennai'],
        topCompanies: ['Amazon', 'Google', 'Microsoft', 'Flipkart', 'Stripe'],
        skills: ['Agile delivery', 'System Architecture', 'Stakeholder Management', 'Risk Mitigation', 'Cross-functional Leadership', 'Jira/Asana'],
        roadmap: ['Agile & Scrum mastery (2-3 weeks)', 'High-level system architecture (3-4 weeks)', 'Cross-functional leadership (2-3 weeks)', 'Risk & resource management (2-3 weeks)', 'Interview & Case Studies (3-4 weeks)'],
        category: 'Management',
        growth: 'High',
        remoteFriendly: true,
    },
    'sre': {
        title: 'Site Reliability Engineer (SRE)',
        description: 'Apply software engineering practices to infrastructure and operations problems to create scalable and highly reliable software systems.',
        salaryRange: { min: 1500000, max: 7000000 },
        avgSalary: 2800000,
        demand: '+30%',
        experience: '3-12+ years',
        topCities: ['Bangalore', 'Pune', 'Hyderabad', 'Gurugram', 'Noida'],
        topCompanies: ['Google', 'LinkedIn', 'Netflix', 'Uber', 'Zomato'],
        skills: ['Linux', 'Python/Go', 'Kubernetes', 'Prometheus', 'Grafana', 'Incident Management', 'SLIs/SLOs'],
        roadmap: ['Linux internals & bash (2-3 weeks)', 'Programming for infrastructure (3-4 weeks)', 'Observability & Monitoring (3-4 weeks)', 'Incident response & blameless postmortems (2-3 weeks)', 'Infrastructure as Code (4-5 weeks)'],
        category: 'Technology',
        growth: 'Very High',
        remoteFriendly: true,
    }
};

const formatSalary = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} LPA`;
    return `₹${(amount / 1000).toFixed(0)}K`;
};

// Career Detail Page
const CareerDetailView = ({ career, slug }) => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#030305] text-white">
            <SEO
                title={`${career.title} Career Path - Salary, Skills & Roadmap | Rastor AI`}
                description={`Become a ${career.title} in India. Average salary: ${formatSalary(career.avgSalary)}. ${career.demand} demand growth. Learn required skills, salary ranges, and get a free personalized roadmap.`}
                keywords={[career.title, `${career.title} salary`, `${career.title} roadmap`, `how to become ${career.title}`, `${career.title} skills India`]}
            />

            {/* Schema */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Occupation",
                    "name": career.title,
                    "description": career.description,
                    "estimatedSalary": {
                        "@type": "MonetaryAmountDistribution",
                        "name": "base",
                        "currency": "INR",
                        "median": career.avgSalary,
                        "percentile10": career.salaryRange.min,
                        "percentile90": career.salaryRange.max
                    },
                    "occupationLocation": career.topCities.map(c => ({ "@type": "City", "name": c })),
                    "skills": career.skills.join(", ")
                })}
            </script>

            {/* Background */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute top-0 left-1/3 w-[800px] h-[800px] bg-gradient-to-br from-emerald-600/15 via-teal-500/8 to-transparent rounded-full blur-[200px]" />
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-4 pt-28 pb-20">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-8">
                    <Link to="/" className="hover:text-white transition-colors">Home</Link>
                    <ChevronRight className="w-4 h-4" />
                    <Link to="/careers" className="hover:text-white transition-colors">Careers</Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-emerald-400">{career.title}</span>
                </div>

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium">{career.category}</span>
                        <span className="flex items-center gap-1 text-sm text-emerald-400 font-medium"><TrendingUp className="w-4 h-4" />{career.demand} demand</span>
                        {career.remoteFriendly && <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-xs">Remote Friendly</span>}
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">How to Become a <span className="text-emerald-400">{career.title}</span> in India</h1>
                    <p className="text-xl text-slate-400 max-w-3xl">{career.description}</p>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Salary Card */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><IndianRupee className="w-5 h-5 text-emerald-400" />Salary Range in India</h2>
                            <div className="grid grid-cols-3 gap-4 mb-6">
                                <div className="text-center p-4 bg-white/5 rounded-xl">
                                    <div className="text-sm text-slate-500 mb-1">Entry Level</div>
                                    <div className="text-xl font-bold text-white">{formatSalary(career.salaryRange.min)}</div>
                                </div>
                                <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                                    <div className="text-sm text-emerald-400 mb-1">Average</div>
                                    <div className="text-xl font-bold text-emerald-400">{formatSalary(career.avgSalary)}</div>
                                </div>
                                <div className="text-center p-4 bg-white/5 rounded-xl">
                                    <div className="text-sm text-slate-500 mb-1">Senior Level</div>
                                    <div className="text-xl font-bold text-white">{formatSalary(career.salaryRange.max)}</div>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-white mb-3">Top Paying Cities</h3>
                                <div className="flex flex-wrap gap-2">
                                    {career.topCities.map((city, i) => (
                                        <span key={i} className="flex items-center gap-1 px-3 py-1.5 bg-white/5 rounded-lg text-sm text-slate-400">
                                            <MapPin className="w-3 h-3" />{city}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Required Skills */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" />Required Skills</h2>
                            <div className="flex flex-wrap gap-2">
                                {career.skills.map((skill, i) => (
                                    <span key={i} className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-slate-300 hover:border-emerald-500/30 hover:text-white transition-colors">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </motion.div>

                        {/* Learning Roadmap */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-blue-400" />Learning Roadmap</h2>
                            <div className="space-y-4">
                                {career.roadmap.map((step, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <div className="w-8 h-8 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center text-emerald-400 text-sm font-bold">{i + 1}</div>
                                            {i < career.roadmap.length - 1 && <div className="w-px h-full bg-white/10 mt-2" />}
                                        </div>
                                        <div className="pb-4">
                                            <p className="text-white font-medium">{step}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Top Companies */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5 text-violet-400" />Top Hiring Companies</h2>
                            <div className="flex flex-wrap gap-3">
                                {career.topCompanies.map((company, i) => (
                                    <div key={i} className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white font-medium">{company}</div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* CTA Card */}
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="sticky top-28">
                            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/20 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-white mb-2">Get Your Personalized Roadmap</h3>
                                <p className="text-sm text-slate-400 mb-4">AI-powered, tailored to your skills and experience. Free to start.</p>
                                <button
                                    onClick={() => navigate('/build-roadmap')}
                                    className="w-full mt-6 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors duration-200 flex pyx-5 items-center justify-center space-x-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                                >
                                    <span>Start Free</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                                <p className="text-[10px] text-slate-500 text-center mt-2">No credit card required • 2 min setup</p>
                            </div>

                            {/* Quick Stats */}
                            <div className="mt-4 bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
                                <h3 className="font-semibold text-white text-sm">Quick Facts</h3>
                                {[
                                    { label: 'Demand Growth', value: career.demand, color: 'text-emerald-400' },
                                    { label: 'Experience', value: career.experience, color: 'text-blue-400' },
                                    { label: 'Growth Potential', value: career.growth, color: 'text-amber-400' },
                                    { label: 'Remote Work', value: career.remoteFriendly ? 'Available' : 'Limited', color: 'text-violet-400' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <span className="text-xs text-slate-500">{item.label}</span>
                                        <span className={`text-sm font-semibold ${item.color}`}>{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Career Explorer List Page
const CareerExplorerPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = ['all', 'Technology', 'Data & AI', 'Product', 'Design', 'Security'];

    const filteredCareers = useMemo(() => {
        return Object.entries(careerDatabase).filter(([key, career]) => {
            const matchesSearch = career.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                career.description.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || career.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, selectedCategory]);

    // If slug is provided, show detail view
    if (slug && careerDatabase[slug]) {
        return <CareerDetailView career={careerDatabase[slug]} slug={slug} />;
    }

    return (
        <div className="min-h-screen bg-[#030305] text-white">
            <SEO
                title="Explore Career Paths - Salary, Skills & Roadmaps | Rastor AI"
                description="Explore 100+ career paths with real salary data, required skills, and step-by-step roadmaps. Find your perfect career path in India. Data Scientist, Software Engineer, Product Manager and more."
                keywords={['career paths India', 'career guide', 'salary data India', 'tech careers', 'how to become software engineer', 'data scientist salary India']}
            />

            {/* Background */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute top-0 left-1/3 w-[800px] h-[800px] bg-gradient-to-br from-emerald-600/15 via-teal-500/8 to-transparent rounded-full blur-[200px]" />
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-4 pt-28 pb-20">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        Explore <span className="text-emerald-400">Career Paths</span>
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Real salary data, required skills, and step-by-step roadmaps for the most in-demand careers in India.
                    </p>
                </motion.div>

                {/* Search & Filter */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <input
                            type="text"
                            placeholder="Search careers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${selectedCategory === cat
                                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                                    : 'bg-white/5 text-slate-400 border border-white/10 hover:text-white'
                                    }`}
                            >
                                {cat === 'all' ? 'All' : cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Career Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCareers.map(([slug, career], i) => (
                        <motion.div
                            key={slug}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                        >
                            <Link
                                to={`/careers/${slug}`}
                                className="block p-6 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-emerald-500/30 hover:bg-white/[0.05] transition-all group"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs font-medium text-emerald-400">{career.category}</span>
                                    <span className="flex items-center gap-1 text-xs text-emerald-400 font-medium"><TrendingUp className="w-3 h-3" />{career.demand}</span>
                                </div>
                                <h2 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">{career.title}</h2>
                                <p className="text-sm text-slate-400 mb-4 line-clamp-2">{career.description}</p>
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-semibold text-white">{formatSalary(career.avgSalary)}<span className="text-xs text-slate-500 font-normal"> avg</span></div>
                                    <span className="text-emerald-400 text-sm font-medium flex items-center gap-1">
                                        View Details
                                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-16 text-center">
                    <div className="bg-gradient-to-r from-emerald-500/10 via-transparent to-teal-500/10 border border-emerald-500/20 rounded-3xl p-12">
                        <h2 className="text-3xl font-bold mb-4">Not Sure Which Career Is Right?</h2>
                        <p className="text-slate-400 mb-8 max-w-xl mx-auto">Our AI analyzes your skills and experience to recommend the perfect career path. Free and takes 2 minutes.</p>
                        <button
                            onClick={() => navigate('/build-roadmap')}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center space-x-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                        >
                            <span>Get AI Career Recommendation</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default CareerExplorerPage;
